import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createApiClient, problemCode, type ApiClient } from '@up2d8/api-client';
import { clearOfflineData } from '../offline/db';
import { isNativeApp } from './native';
import { disableNativePush } from './native-push';
import type { MeResponse, MyPermissions } from '@up2d8/contracts';

/**
 * De sessie in de browser.
 *
 * Het accesstoken staat **alleen in het geheugen**: niet in localStorage, niet in een
 * leesbare cookie. Het refresh token zit in een httpOnly-cookie die JavaScript niet kan
 * lezen; bij het openen van een tab vragen we daarmee stil een nieuw accesstoken op.
 */
/**
 * Welke sessie: die van een organisatie (app en beheer) of die van Master. Sinds MST-10 heeft elk een
 * eigen refresh-cookie, zodat je wisselt zonder af te melden.
 */
export type SessionAudience = 'tenant' | 'platform';

/** Waar de pagina opent, bepaalt welke sessie eerst geprobeerd wordt. */
function audienceOfPath(): SessionAudience {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/master')
    ? 'platform'
    : 'tenant';
}

export interface Session {
  profile: MeResponse | null;
  /**
   * Het profiel zoals het nú is, buiten de React-state om.
   *
   * De poortwachters van de router draaien tussen twee renders in: `profile` kan dan nog
   * de vorige waarde hebben en de gebruiker wordt teruggestuurd naar het aanmeldscherm
   * terwijl hij net is aangemeld. Deze functie leest de ref en heeft dat probleem niet.
   */
  getProfile: () => MeResponse | null;
  api: ApiClient;
  /**
   * Het huidige accesstoken, voor het ene verzoek dat niet door de gegenereerde client
   * kan: de multipart-upload van een importbestand. Alles wat JSON stuurt, gaat via `api`.
   */
  getAccessToken: () => string | null;
  loading: boolean;
  /** Meldt aan binnen een tenant (bedrijfscode + e-mail + wachtwoord). */
  /**
   * Aanmelden met e-mail en wachtwoord (ACC-1). Zit het adres in meer dan één bedrijf, dan komt de
   * keuze terug en is er nog geen sessie; `loginChoose` maakt die.
   */
  /**
   * Aanmelden met e-mail en wachtwoord. `null`: aangemeld. Anders de volgende stap: een bedrijf
   * kiezen, de code uit de authenticator-app (`mfa`), of eerst 2FA instellen (`enrollToken`,
   * FBK-12). Een fout wachtwoord gooit.
   */
  login: (
    input: { tenantCode?: string; email: string; password: string } & SecondFactor,
  ) => Promise<LoginOutcome>;
  loginChoose: (
    chooseToken: string,
    tenantCode: string,
    second?: SecondFactor,
  ) => Promise<null | { mfa: true } | { enrollToken: string }>;
  /** De afgedwongen inschrijving afronden (FBK-12): sessie, en de herstelcodes één keer. */
  completeMfaEnrollment: (enrollToken: string, code: string) => Promise<string[]>;
  /**
   * Meldt aan als platformbeheerder van UP2D8. Geeft 'mfa' terug als het wachtwoord
   * klopte maar de verificatiecode nog ontbreekt; het scherm toont dan het codeveld.
   */
  loginPlatform: (input: {
    email: string;
    password: string;
    totpCode?: string;
    recoveryCode?: string;
  }) => Promise<'ok' | 'mfa' | 'failed' | { enrollToken: string }>;
  /**
   * Activeren met een activatiecode en meteen een pincode kiezen (Stap 5). Geeft de
   * foutmelding terug die de medewerker moet zien, of null als het lukte.
   */
  activate: (input: {
    tenantCode: string;
    employeeNumber: string;
    activationCode: string;
    pin: string;
  }) => Promise<string | null>;
  /** Aanmelden met bedrijfscode, personeelsnummer en pincode. */
  loginWithPin: (input: {
    tenantCode: string;
    employeeNumber: string;
    pin: string;
  }) => Promise<string | null>;
  logout: () => Promise<void>;
  /**
   * Een sessie overnemen die een ander scherm al kreeg (REG-2): na het registreren met een
   * uitnodiging geeft de server meteen een accesstoken terug, net als na activeren.
   */
  adoptToken: (accessToken: string) => Promise<void>;
  /** Haalt het profiel op met het refresh-cookie; true als er een sessie was. */
  restore: (audience?: SessionAudience) => Promise<boolean>;
  /**
   * Heeft de aangemelde gebruiker dit recht? Uitsluitend om navigatie en knoppen te
   * verbergen: de server toetst elke aanroep opnieuw. Een verborgen knop is geen slot.
   */
  has: (policy: string) => boolean;
  /**
   * De rechten zijn minstens één keer opgehaald voor dit profiel (FBK-2). Vóór dat moment geeft
   * `has` voor alles `false`, en een scherm dat daarop "Geen toegang" toont, zou even flitsen.
   */
  permissionsReady: boolean;
  /** Na het wijzigen van je eigen rollen: de rechten opnieuw ophalen. */
  refreshPermissions: () => Promise<void>;
}

/** De tweede stap bij het aanmelden (FBK-12): een code uit de app, of een herstelcode. */
export interface SecondFactor {
  totpCode?: string;
  recoveryCode?: string;
}

export type LoginOutcome =
  | null
  | { choose: { tenantCode: string; name: string }[]; chooseToken: string }
  | { mfa: true }
  | { enrollToken: string };

const SessionContext = createContext<Session | null>(null);

/**
 * De paden in het gegenereerde contract bevatten het volledige prefix (/api/v1/...),
 * dus de basis is de origin zelf. Alleen bij een aparte API-host is deze variabele nodig.
 */
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export function SessionProvider({ children }: { children: ReactNode }) {
  const accessToken = useRef<string | null>(null);
  const profileRef = useRef<MeResponse | null>(null);
  /** De lopende vernieuwing, zodat er nooit twee tegelijk met hetzelfde cookie gaan. */
  const refreshInFlight = useRef<Promise<'ok' | 'geweigerd' | 'offline'> | null>(null);
  /** De cookie waarmee vernieuwd wordt (MST-10). */
  const audienceRef = useRef<SessionAudience>(audienceOfPath());
  const queryClient = useQueryClient();
  const [profile, setProfileState] = useState<MeResponse | null>(null);
  const [policyKeys, setPolicyKeys] = useState<readonly string[]>([]);
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [loading, setLoading] = useState(false);

  // Ref en state samen bijwerken: de ref voor de router, de state voor de weergave.
  const setProfile = useCallback((next: MeResponse | null) => {
    profileRef.current = next;
    setProfileState(next);
  }, []);

  /**
   * Haalt een nieuw accesstoken op met het refresh-cookie.
   *
   * Het onderscheid tussen "de server zegt nee" en "er is geen netwerk" is hier de hele zaak
   * (Stap 12). Een medewerker in een koelcel hoort niet uitgelogd te worden omdat zijn telefoon geen
   * bereik heeft: dan zou hij bij elke pauze opnieuw zijn pincode moeten intoetsen, en dat is precies
   * hoe een app die offline hoort te werken alsnog onbruikbaar wordt.
   *
   * Daarom: alleen bij een **antwoord** van de server verdwijnt het token. Een `fetch` die gooit, is
   * geen antwoord — dan houden we wat we hebben en proberen we het later.
   */
  /**
   * Vernieuwt de sessie, en precies één keer tegelijk.
   *
   * Het bundelen is geen optimalisatie maar een noodzaak. Een refresh token is eenmalig: bij hergebruik
   * gaat de server ervan uit dat het gestolen is en trekt hij **alle** sessies van die persoon in
   * (Stap 5). Twee verzoeken die tegelijk een `401` krijgen — en dat gebeurt zodra een medewerker na een
   * offline periode weer bereik heeft: de feed, zijn standen en de wachtrij lopen dan samen — zouden
   * dus samen hetzelfde cookie aanbieden en hem uitloggen.
   *
   * Vandaar: wie vraagt terwijl er al een poging loopt, krijgt diezelfde poging terug.
   */
  const refreshWithReason = useCallback((): Promise<'ok' | 'geweigerd' | 'offline'> => {
    refreshInFlight.current ??= doRefresh().finally(() => {
      refreshInFlight.current = null;
    });
    return refreshInFlight.current;
  }, []);

  const doRefresh = useCallback(async (): Promise<'ok' | 'geweigerd' | 'offline'> => {
    let response: Response;
    try {
      response = await fetch(`${API_BASE}/api/v1/auth/refresh?audience=${audienceRef.current}`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Geen antwoord is geen oordeel: dit is een telefoon zonder bereik.
      return 'offline';
    }

    if (!response.ok) {
      accessToken.current = null;
      // Ook profiel, rechten en cache weg: anders laat de poortwachter van de router de gebruiker
      // binnen in een zone waar elk verzoek mislukt.
      setProfile(null);
      setPolicyKeys([]);
      setPermissionsReady(false);
      queryClient.clear();
      // De server heeft geoordeeld: deze sessie bestaat niet meer. Alles wat lokaal staat, gaat weg
      // (ADR-0046) — een ingetrokken sessie mag geen berichten op een toestel laten staan. Master
      // bewaart niets offline: geen Master-sessie zegt niets over die van de organisatie.
      if (audienceRef.current === 'tenant') await clearOfflineData();
      return 'geweigerd';
    }

    const body = (await response.json()) as { accessToken: string };
    accessToken.current = body.accessToken;
    return 'ok';
  }, [setProfile, queryClient]);

  const refresh = useCallback(
    async (): Promise<boolean> => (await refreshWithReason()) === 'ok',
    [refreshWithReason],
  );

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: API_BASE,
        getAccessToken: () => accessToken.current,
        // Bij een 401 vernieuwt de client stil en probeert het verzoek één keer opnieuw.
        onUnauthorized: refresh,
      }),
    [refresh],
  );

  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.GET('/api/v1/me/permissions');
      const keys = (data as MyPermissions | undefined)?.policyKeys;
      // Zonder netwerk komt hier het antwoord van de vorige keer uit de cache van de service worker.
      // Mislukt ook dat, dan houden we de rechten die we hadden: ze op leeg zetten zou de app offline
      // uitkleden tot een scherm zonder knoppen.
      if (keys) setPolicyKeys(keys);
    } catch {
      // Idem: houden wat we hebben.
    }
    setPermissionsReady(true);
  }, [api]);

  const loadProfile = useCallback(async () => {
    // Zonder netwerk serveert de service worker het profiel van de vorige keer (ADR-0046); daarmee
    // opent de app in vliegtuigmodus op de feed en niet op het aanmeldscherm.
    const { data } = await api.GET('/api/v1/auth/me');
    const next = (data as MeResponse | undefined) ?? null;
    setProfile(next);
    // Rechten zitten sinds Stap 4 niet meer in het token of het profiel: ze komen uit
    // access, zodat een rolwijziging geldt zonder opnieuw aan te melden.
    if (next) await loadPermissions();
    else {
      setPolicyKeys([]);
      setPermissionsReady(false);
    }
  }, [api, setProfile, loadPermissions]);

  const login: Session['login'] = useCallback(
    async (input) => {
      setLoading(true);
      try {
        audienceRef.current = 'tenant';
        const { data, error } = await api.POST('/api/v1/auth/login', { body: input as never });
        // Het wachtwoord klopte, de code uit de app ontbreekt nog (FBK-12): geen fout, een stap.
        if (problemCode(error) === 'MFA_REQUIRED') return { mfa: true as const };
        if (error || !data) throw error ?? new Error('Aanmelden is niet gelukt.');
        const result = data as {
          accessToken?: string;
          choose?: { tenantCode: string; name: string }[];
          chooseToken?: string;
          enrollToken?: string;
        };
        if (result.enrollToken) return { enrollToken: result.enrollToken };
        if (result.choose && result.chooseToken) {
          return { choose: result.choose, chooseToken: result.chooseToken };
        }
        // Een nieuwe gebruiker ziet niets uit de cache van de vorige (profiel, thema, lijsten).
        queryClient.clear();
        accessToken.current = result.accessToken ?? null;
        await loadProfile();
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, loadProfile, queryClient],
  );

  const loginChoose: Session['loginChoose'] = useCallback(
    async (chooseToken, tenantCode, second) => {
      setLoading(true);
      try {
        audienceRef.current = 'tenant';
        const { data, error } = await api.POST('/api/v1/auth/login/choose', {
          body: { chooseToken, tenantCode, ...second } as never,
        });
        if (problemCode(error) === 'MFA_REQUIRED') return { mfa: true as const };
        if (error || !data) throw error ?? new Error('Aanmelden is niet gelukt.');
        const result = data as { accessToken?: string; enrollToken?: string };
        if (result.enrollToken) return { enrollToken: result.enrollToken };
        queryClient.clear();
        accessToken.current = result.accessToken ?? null;
        await loadProfile();
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, loadProfile, queryClient],
  );

  const completeMfaEnrollment: Session['completeMfaEnrollment'] = useCallback(
    async (enrollToken, code) => {
      setLoading(true);
      try {
        const { data, error } = await api.POST('/api/v1/auth/mfa/enroll/confirm', {
          body: { enrollToken, code } as never,
        });
        if (error || !data) throw error ?? new Error('Instellen is niet gelukt.');
        const result = data as {
          accessToken: string;
          recoveryCodes: string[];
          audience: 'tenant' | 'platform';
        };
        audienceRef.current = result.audience;
        queryClient.clear();
        accessToken.current = result.accessToken;
        await loadProfile();
        return result.recoveryCodes;
      } finally {
        setLoading(false);
      }
    },
    [api, loadProfile, queryClient],
  );

  const loginPlatform: Session['loginPlatform'] = useCallback(
    async (input) => {
      setLoading(true);
      try {
        audienceRef.current = 'platform';
        queryClient.clear();
        const { data, error } = await api.POST('/api/v1/platform/auth/login', {
          body: input as never,
        });
        if (error || !data) {
          return problemCode(error) === 'MFA_REQUIRED' ? 'mfa' : 'failed';
        }
        const result = data as { accessToken?: string; enrollToken?: string };
        // Nog geen 2FA (FBK-12): eerst de authenticator-app koppelen.
        if (result.enrollToken) return { enrollToken: result.enrollToken };
        accessToken.current = result.accessToken ?? null;
        await loadProfile();
        return 'ok';
      } finally {
        setLoading(false);
      }
    },
    [api, loadProfile, queryClient],
  );

  /**
   * Eén weg voor activeren en aanmelden met een pincode: beide leveren een sessie op, en
   * beide moeten bij een fout één begrijpelijke zin teruggeven in plaats van een
   * technische melding.
   */
  const submitAndStart = useCallback(
    async (path: '/api/v1/auth/activate' | '/api/v1/auth/login/pin', body: unknown) => {
      setLoading(true);
      try {
        audienceRef.current = 'tenant';
        const { data, error } = await api.POST(path, { body: body as never });
        if (error || !data) {
          const code = problemCode(error);
          if (code === 'RATE_LIMITED') return 'locked';
          if (code === 'VALIDATION_FAILED') {
            const first = (error as { errors?: { message: string }[] } | undefined)?.errors?.[0];
            return first?.message ?? 'invalid';
          }
          return 'failed';
        }
        queryClient.clear();
        accessToken.current = (data as { accessToken: string }).accessToken;
        await loadProfile();
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, loadProfile, queryClient],
  );

  const activate: Session['activate'] = useCallback(
    (input) => submitAndStart('/api/v1/auth/activate', input),
    [submitAndStart],
  );

  const loginWithPin: Session['loginWithPin'] = useCallback(
    (input) => submitAndStart('/api/v1/auth/login/pin', input),
    [submitAndStart],
  );

  const adoptToken = useCallback(
    async (token: string) => {
      audienceRef.current = 'tenant';
      queryClient.clear();
      accessToken.current = token;
      await loadProfile();
    },
    [loadProfile, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      // Eerst het toestel afmelden, zolang de sessie nog geldt: wie uitlogt, krijgt op deze telefoon
      // geen meldingen meer voor dit account (MOB-1).
      if (isNativeApp()) await disableNativePush(api);
      // Alleen deze kant: afmelden bij Master laat de organisatie open, en omgekeerd (MST-10).
      await api.POST('/api/v1/auth/logout', {
        params: { query: { audience: audienceRef.current } } as never,
      });
    } catch {
      // Offline gooit de aanroep. Lokaal afmelden moet dan toch doorgaan; de sessie op de server
      // verloopt vanzelf.
    }
    accessToken.current = null;
    setProfile(null);
    setPolicyKeys([]);
    setPermissionsReady(false);
    queryClient.clear();
    // Uitloggen betekent: er staat niets meer van dit bedrijf op dit toestel (ADR-0046). Ook de
    // wachtrij gaat mee — wie uitlogt voordat zijn bevestiging aankwam, heeft die niet gegeven op een
    // manier die wij kunnen bewijzen, en dan is stil bewaren erger dan weggooien.
    await clearOfflineData();
  }, [api, setProfile, queryClient]);

  /**
   * Haalt de sessie terug bij het openen van een tab.
   *
   * Met netwerk is dat één verzoek: vernieuwen, en dan het profiel. Zonder netwerk is het de kern van
   * Stap 12: een medewerker in een koelcel hoort zijn berichten te kunnen lezen, en dus mag "ik kan het
   * token niet vernieuwen" hem niet naar het aanmeldscherm sturen.
   *
   * Dan vertrouwen we op wat de service worker bewaarde: het profiel en de rechten van de vorige keer
   * (ADR-0046). Er is geen accesstoken, dus elk verzoek naar de server mislukt — en dat is precies waar
   * de lokale kopie voor is. Zodra er weer bereik is, vernieuwt de client stil en werkt alles weer.
   *
   * Is de sessie **geweigerd** (een uitdienst, een ingetrokken sessie), dan gebeurt het omgekeerde: alles
   * lokaal is al gewist en dit levert `false` op.
   */
  const restore = useCallback(
    async (audience?: SessionAudience) => {
      setLoading(true);
      try {
        // Wisselen tussen een organisatie en Master (MST-10): de andere cookie, en niets van de vorige
        // kant blijft staan - geen token, geen profiel, geen rechten, geen cache.
        if (audience && audience !== audienceRef.current) {
          audienceRef.current = audience;
          accessToken.current = null;
          setProfile(null);
          setPolicyKeys([]);
          setPermissionsReady(false);
          queryClient.clear();
        }
        const reason = await refreshWithReason();
        if (reason === 'geweigerd') return false;

        await loadProfile();
        if (profileRef.current) return true;

        // Offline en niets in de cache: dan is er niets om te herstellen.
        return false;
      } catch {
        return false;
      } finally {
        setLoading(false);
      }
    },
    [refreshWithReason, loadProfile, setProfile, queryClient],
  );

  const value = useMemo<Session>(
    () => ({
      profile,
      getProfile: () => profileRef.current,
      api,
      getAccessToken: () => accessToken.current,
      loading,
      login,
      loginChoose,
      completeMfaEnrollment,
      loginPlatform,
      activate,
      loginWithPin,
      logout,
      adoptToken,
      restore,
      // De client verbergt wat je niet mag; de server weigert het (dat is de echte grens).
      has: (policy) => policyKeys.includes(policy),
      permissionsReady,
      refreshPermissions: loadPermissions,
    }),
    [
      profile,
      policyKeys,
      permissionsReady,
      api,
      loading,
      login,
      loginChoose,
      completeMfaEnrollment,
      loginPlatform,
      activate,
      loginWithPin,
      logout,
      adoptToken,
      restore,
      loadPermissions,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession moet binnen een <SessionProvider> gebruikt worden.');
  return session;
}
